/**
 * Utility functions for handling URL-related operations
 */

/**
 * Get the full name of a user
 * @param {Object} user - The user object
 * @returns {string} The full name of the user
 */
export const getFullName = (user) => {
  return [user.firstName, user.middleName, user.lastName]
    .filter(Boolean)
    .join(" ");
};

/**
 * Get the initial of a user's name
 * @param {Object} user - The user object
 * @returns {string} The initial of the user's name
 */
export const getInitial = (user) => {
  if (user.firstName) {
    return user.firstName.charAt(0);
  }
  if (user.name) {
    return user.name.charAt(0);
  }
  return "";
};

/**
 * Get the student profile URL
 * @param {Object} student - The student object
 * @returns {string} The student profile URL
 */
export const getStudentProfileUrl = (student) => {
  return `/dashboard/students/${student.username}`;
};

/**
 * Get the teacher profile URL
 * @param {Object} teacher - The teacher object
 * @returns {string} The teacher profile URL
 */
export const getTeacherProfileUrl = (teacher) => {
  return `/dashboard/teachers/${teacher.username}`;
};

/**
 * Get the staff profile URL
 * @param {Object} staff - The staff object
 * @returns {string} The staff profile URL
 */
export const getStaffProfileUrl = (staff) => {
  return `/dashboard/staff/${staff.username}`;
};

/**
 * Find a user by username
 * @param {Array} users - Array of user objects
 * @param {string} username - The username to search for
 * @returns {Object|null} The found user object or null
 */
export const findUserByUsername = (users, username) => {
  return users.find((user) => user.username === username) || null;
};

/**
 * Get a resized/optimized Cloudinary thumbnail URL for a given image URL.
 * Works on-the-fly for existing images too, no reprocessing needed.
 * @param {string} url - The original Cloudinary image URL
 * @param {number} [size=80] - Width/height in pixels for the thumbnail
 * @returns {string} The transformed thumbnail URL, or the original url unchanged if not a Cloudinary upload URL
 */
export const getCloudinaryThumbnail = (url, size = 80) => {
  if (!url || !url.includes("/upload/")) return url;
  return url.replace("/upload/", `/upload/w_${size},h_${size},c_fill,q_auto,f_auto/`);
};